"use client";

import { useEffect, useState } from "react";
import Icon from "@/components/ui/AppIcon";
import IPTypeBadge from "@/components/ui/IPTypeBadge";
import { apiFetch } from "@/lib/api/client";
import type { DashboardSummary } from "../types";

type Props = { selectedDate: string };

export default function RecentActivityFeed({ selectedDate }: Props) {
  const [data, setData] = useState<DashboardSummary['recentActivity'] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const queryParams = selectedDate ? `?date=${selectedDate}` : "";
        const res = await apiFetch<DashboardSummary['recentActivity']>(`/api/v1/dashboard/activity${queryParams}`, { method: "GET", auth: true });
        if (active) setData(res);
      } catch (error) {
        console.error("Failed to load recent activity data:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [selectedDate]);

  const activities = data ?? [];

  if (loading || !data) return <div className="bg-white rounded-xl border border-border shadow-card p-5 h-[420px] animate-pulse" />;

  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-5 h-full max-h-[500px] flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h3 className="text-[15px] font-600 text-foreground">Recent Activity</h3>
          <p className="text-[12px] text-muted-foreground mt-0.5">All team actions across cases</p>
        </div>
      </div>

      {activities.length === 0 ? (
        <div className="py-10 flex flex-col items-center justify-center text-muted-foreground flex-1">
          <Icon name="InboxIcon" size={32} className="mb-3 opacity-30" />
          <p className="text-[13px] font-500 text-foreground">No recent activity</p>
          <p className="text-[12px] mt-1">Check back later for updates</p>
        </div>
      ) : (
        <ul className="space-y-0 divide-y divide-border overflow-y-auto flex-1 pr-1 custom-scrollbar">
          {activities.map((activity) => (
            <li key={activity.id} className="flex items-start gap-3 py-3 hover:bg-muted/30 -mx-1 px-1 rounded-lg transition-colors duration-100 cursor-default group">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-700 mt-0.5 ${activity.actorColor}`}>
                {activity.actorInitials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-foreground leading-snug">
                  <span className="font-600">{activity.actor}</span>{" "}
                  <span className="text-muted-foreground">{activity.action}</span>{" "}
                  <span className="font-mono text-[11px] font-600 text-primary">{activity.entity}</span>
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{activity.detail}</p>
                <div className="flex items-center gap-2 mt-1">
                  <IPTypeBadge type={activity.entityType} size="sm" showIcon={false} />
                  <span className="text-[10px] text-muted-foreground">{activity.time}</span>
                </div>
              </div>
              <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${activity.iconColor}`}>
                <Icon name={activity.icon as any} size={12} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
